/**
 * Single source of truth for admin-panel access rules, shared by the Sidebar
 * (menu visibility) and the router (entry gate + route guards) so the two never
 * drift apart.
 *
 * Model:
 *  - `admin`       → full access (every menu item + every route)
 *  - `coordinator` → limited to the whitelisted top-level sections below
 *  - everyone else → no access to the panel
 */

// Structural user shape — satisfied by both the auth store `User` and the API
// `UserProfile`, so callers can pass either without coupling this module to one.
interface UserLike {
  groups?: { name: string }[];
}

// Groups allowed to log into the panel at all.
export const PANEL_GROUPS: readonly string[] = ['admin', 'coordinator'];

// Top-level paths a coordinator (non-admin) may see in the menu and reach.
// '/reports' covers every /reports/* page — all are gated by `can_access_reports`
// on the backend, which coordinators have.
// Note: '/meals' is intentionally excluded — coordinators lack the backend
// `can_manage_meals` permission, so that section would 403 for them.
export const COORDINATOR_NAV_HREFS: readonly string[] = [
  '/bookings',
  '/institutions',
  '/tours',
  '/extras',
  '/reports',
];

export const isAdmin = (user: UserLike | null | undefined): boolean =>
  !!user?.groups?.some((g) => g.name === 'admin');

// Whether the user may enter the panel at all (admin or coordinator).
export const hasPanelAccess = (user: UserLike | null | undefined): boolean =>
  !!user?.groups?.some((g) => PANEL_GROUPS.includes(g.name));

// Whether a non-admin may access the given path: a whitelisted top-level
// section or any route nested under it (e.g. `/bookings/new`, `/meals/items/1`).
export const isCoordinatorAllowedPath = (pathname: string): boolean =>
  COORDINATOR_NAV_HREFS.some((href) => pathname === href || pathname.startsWith(`${href}/`));
